/**
 * SVG extraction utilities for PDF chart embedding
 */

/**
 * Extracts and serializes an SVG element from a container
 * @param container - DOM element containing the SVG
 * @returns Serialized SVG string or null if not found
 */
export function extractSVGFromElement(container: HTMLElement): string | null {
  const svg = container.querySelector('svg');
  if (!svg) return null;

  // Clone to avoid modifying the original
  const clonedSvg = svg.cloneNode(true) as SVGElement;

  // Ensure SVG has proper namespace
  if (!clonedSvg.getAttribute('xmlns')) {
    clonedSvg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
  }

  // Add xlink namespace for any href attributes
  if (!clonedSvg.getAttribute('xmlns:xlink')) {
    clonedSvg.setAttribute('xmlns:xlink', 'http://www.w3.org/1999/xlink');
  }

  // Get computed dimensions if not set
  const width = clonedSvg.getAttribute('width') || svg.getBoundingClientRect().width.toString();
  const height = clonedSvg.getAttribute('height') || svg.getBoundingClientRect().height.toString();

  if (!clonedSvg.getAttribute('width')) {
    clonedSvg.setAttribute('width', width);
  }
  if (!clonedSvg.getAttribute('height')) {
    clonedSvg.setAttribute('height', height);
  }

  // Serialize to string
  const serializer = new XMLSerializer();
  return serializer.serializeToString(clonedSvg);
}

/**
 * Parses an SVG string into an SVGElement
 * @param svgString - Serialized SVG string
 * @returns Parsed SVGElement
 */
export function parseSVGString(svgString: string): SVGElement {
  const parser = new DOMParser();
  const doc = parser.parseFromString(svgString, 'image/svg+xml');
  return doc.documentElement as unknown as SVGElement;
}

/**
 * Gets the dimensions of an SVG element
 * @param svg - SVG element or string
 * @returns Object with width and height
 */
export function getSVGDimensions(svg: SVGElement | string): { width: number; height: number } {
  const element = typeof svg === 'string' ? parseSVGString(svg) : svg;

  // Try to get from attributes first
  const widthAttr = element.getAttribute('width');
  const heightAttr = element.getAttribute('height');

  if (widthAttr && heightAttr) {
    const width = parseFloat(widthAttr);
    const height = parseFloat(heightAttr);
    if (!isNaN(width) && !isNaN(height)) {
      return { width, height };
    }
  }

  // Try viewBox
  const viewBox = element.getAttribute('viewBox');
  if (viewBox) {
    const parts = viewBox.split(/\s+|,/).map(Number);
    if (parts.length === 4 && !isNaN(parts[2]) && !isNaN(parts[3])) {
      return { width: parts[2], height: parts[3] };
    }
  }

  // Default fallback
  return { width: 500, height: 300 };
}

/**
 * Scales SVG dimensions to fit within max constraints while preserving aspect ratio
 * @param width - Original width
 * @param height - Original height
 * @param maxWidth - Maximum allowed width
 * @param maxHeight - Maximum allowed height
 * @returns Scaled dimensions
 */
export function scaleSVGToFit(
  width: number,
  height: number,
  maxWidth: number,
  maxHeight: number
): { width: number; height: number; scale: number } {
  const scaleX = maxWidth / width;
  const scaleY = maxHeight / height;
  const scale = Math.min(scaleX, scaleY, 1); // Don't upscale

  return {
    width: width * scale,
    height: height * scale,
    scale,
  };
}
